<script setup lang="ts">
import { email as emailRule, required } from '@vee-validate/rules';
import { Form } from 'vee-validate';
import { ref } from 'vue';
import { useStore } from 'vuex';

import { subscribeCustomer } from '@/api/contacts';
import { applyDiscountCodeToken } from '@/api/discounts';
import FormInput from '@/components/base/form/FormInput.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import ToastNotification from '@/components/base/ToastNotification.vue';
import Caption from '@/components/base/typography/Caption.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { useOnsiteOffer } from '@/composables/personalization/useOnsiteOffer';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';
import { USER_EMAIL_COOKIE } from '@/lib/personalization/common';
import { ButtonClickedPayload, sendButtonClicked } from '@/utils/analytics/buttonClicked';
import { sendContactSubscribed } from '@/utils/analytics/contactSubscribed';
import { setCookie } from '@/utils/isomorphic/cookie';
import { reportError } from '@/utils/reportError';

const { couponToken, pageSection } = defineProps<{
  couponToken: string;
  pageSection: ButtonClickedPayload['pageSection'];
}>();

const emit = defineEmits<{
  back: [];
  'newsletter-signup-completed': [string];
}>();

const notificationMessage = ref<string>();
const emailInput = ref<string>();

const store = useStore();
const { setCart } = useCart(store);
const { errorMessages, validatorFailed } = useForm();
const { results, waitForStorageUpdate } = useOnsiteOffer();

const rules: Record<'email', FlexibleMessageValidator<string>> = {
  email: (value) => {
    if (!required(value)) return errorMessages.email.required;
    if (!emailRule(value)) return errorMessages.email.email;
    return true;
  },
};

async function applyCoupon() {
  let response;
  try {
    response = await applyDiscountCodeToken(couponToken, (res) => {
      // couponResponse will hold errors which we can show to the user on the results page
      results.value.couponResponse = res;
    });
  } catch (error) {
    reportError(error);
  }

  if (response?.cart) {
    setCart(response.cart);
  }
}

const onSubmit = useCallback(async () => {
  try {
    const email = emailInput.value;

    // email is guaranteed by form validation, so this is just to appease TS
    if (!email) return;

    const response = await subscribeCustomer(email, 'Onsite Offer Quiz');

    if (response.existingCustomer === true || response.existingContact === true) {
      results.value.existingContact = true;
    } else {
      results.value.existingContact = false;
      await applyCoupon();
      setCookie(USER_EMAIL_COOKIE, email);
    }

    sendContactSubscribed({
      email,
      reason: 'Onsite Offer Quiz',
      source: 'Promo Selection',
    });

    await waitForStorageUpdate();

    emit('newsletter-signup-completed', email);
  } catch (error) {
    reportError(error);
    notificationMessage.value = 'Oops... Something went wrong. Please try again.';
  }
});

function handleBack() {
  sendButtonClicked({
    buttonName: `Newsletter Signup Back button`,
    content: 'Back',
    pageSection,
  });
  emit('back');
}
</script>

<template>
  <div class="relative pt-6 pb-36">
    <div class="flex flex-col items-center px-4">
      <Header4 tag="p" class="text-center custom-gelica">
        Yay! You've earned 20% off your first order!
      </Header4>
      <Caption class="mt-2.5 text-center">
        Almost there! Enter your email to redeem the offer.
      </Caption>
      <div class="w-full max-w-md sm:max-w-none sm:w-2/3 mt-2.5">
        <Form v-slot="{ meta: formEmailMeta }" @submit="onSubmit.execute()" class="grid gap-3">
          <FormInput
            v-model="emailInput"
            class="w-full"
            :inputAttributes="{
              autocomplete: 'email',
              name: 'email',
              placeholder: 'Enter Email Address',
              type: 'email',
            }"
            showLabel
            showPlaceholder
            theme="dark"
            :validator="rules.email"
          />
          <ThemedButton
            class="w-full h-12"
            :disabled="(formEmailMeta.touched && validatorFailed(formEmailMeta)) ?? false"
            :isLoading="onSubmit.isPending"
            size="default"
            theme="gray"
            type="submit"
          >
            Claim Offer & View Quiz Result
          </ThemedButton>
          <Teleport v-if="!!notificationMessage" to="#teleported">
            <ToastNotification :show="!!notificationMessage" @hide="notificationMessage = ''">
              <SmallBodyText>{{ notificationMessage }}</SmallBodyText>
            </ToastNotification>
          </Teleport>
        </Form>
        <Caption class="mt-2 text-center">
          Valid for first-time subscribers only. Shipping to destinations within the contiguous USA.
          Free shipping applies to standard, “ground” shipping methods. Offer is not applicable to
          e-gift certificates, wholesale items (cases), or multiple address orders. Must spend at
          least $59 after the discount is applied to qualify for free shipping.
        </Caption>
      </div>
    </div>
    <div
      class="fixed bottom-0 left-0 right-0 flex p-4 mx-auto bg-white border-t border-solid sm:absolute border-neutral-100"
    >
      <ThemedButton theme="white" size="large" @click="handleBack">Back</ThemedButton>
    </div>
  </div>
</template>

<style scoped>
.custom-gelica {
  @apply font-gelica;
}
</style>
